// ** React Imports
import { SyntheticEvent, useEffect, useState } from "react";

// ** MUI Imports
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import { TabList, TabPanel } from "@mui/lab";
import Avatar from "@mui/material/Avatar";
import TabContext from "@mui/lab/TabContext";
import Typography from "@mui/material/Typography";
import CardContent from "@mui/material/CardContent";
import { Theme, useTheme } from "@mui/material/styles";
import Chart from "react-apexcharts";

// ** Third Party Imports
import { ApexOptions } from "apexcharts";

// ** Custom Components Import

// ** Util Import
import { CardHeader } from "@mui/material";
import _ from "lodash";
import { BarUnit, LocateTabs } from "../../common/constants";
import { formatNumber_q, hexToRGBA } from "../../common/commonFunctions";
import { Icon } from "@iconify/react";

type ApexChartSeries = NonNullable<ApexOptions["series"]>;
type ApexChartSeriesData = Exclude<ApexChartSeries[0], number>;

type TabCategory = "Locates-Approved" | "Locates-Used" | "Pre-Borrow";

interface QuoteBarCHartInfoProps {
  data: BarUnit[];
}

const LocatesBarChart = (props: QuoteBarCHartInfoProps) => {
  // ** State
  const [currentTab, setCurrentTab] = useState<TabCategory>("Locates-Approved");

  // ** Hook
  const theme = useTheme();

  const [isChartVisible, setIsChartVisible] = useState<boolean>(true);

  const [height, setHeight] = useState<number>(320);

  const [tabData, setTabData] = useState<any>([
    {
      type: "Locates-Approved",
      avatarIcon: "marketeq:file-folder-approved-2",
      series: [{ data: [] }],
      labels: [],
    },
    {
      type: "Locates-Used",
      avatarIcon: "material-symbols-light:energy-program-time-used-outline",
      series: [{ data: [] }],
      labels: [],
    },
    {
      type: "Pre-Borrow",
      avatarIcon: "arcticons:borrowed",
      series: [{ data: [] }],
      labels: [],
    },
  ]);

  const [xAxisData, setXAxisData] = useState<any>(tabData[0].labels);

  useEffect(() => {
    const data = props.data;
    if (!_.isEqual(data, tabData)) {
      const newTabData = [
        {
          type: LocateTabs.locatesApproved,
          avatarIcon: "marketeq:file-folder-approved-2",
          series: data.find((t) => t.type === LocateTabs.locatesApproved)
            ?.series,
          labels: data.find((t) => t.type === LocateTabs.locatesApproved)
            ?.labels,
        },
        {
          type: LocateTabs.locatesUsed,
          avatarIcon: "material-symbols:energy-program-time-used-outline",
          series: data.find((t) => t.type === LocateTabs.locatesUsed)?.series,
          labels: data.find((t) => t.type === LocateTabs.locatesUsed)?.labels,
        },
        {
          type: LocateTabs.preBorrow,
          avatarIcon: "arcticons:borrowed",
          series: data.find((t) => t.type === LocateTabs.preBorrow)?.series,
          labels: data.find((t) => t.type === LocateTabs.preBorrow)?.labels,
        },
      ];
      setTabData(newTabData);
    }
  }, [props.data]);

  useEffect(() => {
    setIsChartVisible(false);
  }, [tabData]);

  useEffect(() => {
    if (!isChartVisible) setIsChartVisible(true);
  }, [isChartVisible]);

  useEffect(() => {
    const tab = tabData.find((t: any) => t.type === currentTab);
    setXAxisData(tab.labels);
  }, [currentTab, tabData]);

  const renderTabs = (value: TabCategory, theme: Theme) => {
    return tabData.map((item: any, index: any) => {
      const RenderAvatar = item.type === value ? Avatar : Avatar;

      return (
        <Tab
          key={index}
          value={item.type}
          className="pt-1"
          label={
            <Box
              sx={{
                borderWidth: 1,
                display: "flex",
                alignItems: "center",
                borderRadius: "10px",
                flexDirection: "row",
                justifyContent: "center",
                borderStyle: "solid",
                borderColor:
                  item.type === value ? "#1E355E" : `rgba(47, 43, 61, 0.16)`,
              }}
            >
              <RenderAvatar
                variant="rounded"
                {...(item.type === value && { skin: "light" })}
                sx={{
                  borderRadius: "10px",
                  width: 34,
                  height: 34,
                  ...(item.type !== value && {
                    backgroundColor: "action.selected",
                  }),
                }}
              >
                <Icon
                  icon={item.avatarIcon}
                  color={
                    item.type === value ? "#1E355E" : `rgba(47, 43, 61, 0.20)`
                  }
                ></Icon>
              </RenderAvatar>
              <Typography
                sx={{
                  paddingX: 1,
                  fontWeight: 500,
                  color: "text.secondary",
                  textTransform: "capitalize",
                }}
              >
                {item.type}
              </Typography>
            </Box>
          }
        />
      );
    });
  };

  useEffect(() => {
    // Function to handle the resize event
    function handleResize() {
      if (window?.innerWidth > 3500) {
        setHeight(700);
      } else if (window?.innerWidth > 2500) {
        setHeight(400);
      } else if(window?.innerWidth > 2000){
        setHeight(320);
      }
      else{
        setHeight(280);
      }
    }

    // Add the event listener
    window.addEventListener("resize", handleResize);

    // Cleanup the event listener on component unmount
    return () => window.removeEventListener("resize", handleResize);
  }, []);

useEffect(()=>{
  renderTabPanels(currentTab, theme, options, colors);
},[height])

  const renderTabPanels = (
    value: TabCategory,
    theme: Theme,
    options: ApexOptions,
    colors: string[]
  ) => {
    return tabData.map((item: any, index: any) => {
      const max = Math.max(
        ...((item.series[0] as ApexChartSeriesData).data as number[])
      );
      const seriesIndex = (
        (item.series[0] as ApexChartSeriesData).data as number[]
      ).indexOf(max);

      const finalColors = colors.map((color, i) =>
        seriesIndex === i ? hexToRGBA("#1E355E", 1) : color
      );

      return (
        <TabPanel
          className="p-0 pt-1"
          sx={{ height: "2vh" }}
          key={index}
          value={item.type}
          id='locate-panel'
        >
          <Chart
            type="bar"
            height={height}
            options={{ ...options, colors: finalColors }}
            series={item.series}
          />
        </TabPanel>
      );
    });
  };

  const handleChange = (event: SyntheticEvent, newValue: TabCategory) => {
    setCurrentTab(newValue);
  };

  const colors = Array(9).fill(hexToRGBA("#1E355E", 0.16));

  const options: ApexOptions = {
    chart: {
      parentHeightOffset: 0,
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        borderRadius: 6,
        distributed: true,
        columnWidth: "20%",
        //startingShape: 'rounded',
        dataLabels: { position: "top" },
      },
    },
    legend: { show: false },
    tooltip: { enabled: false },
    dataLabels: {
      offsetY: -30,
      formatter: (val) => (_.isNumber(val) ? formatNumber_q.format(val) : ""),
      style: {
        fontWeight: 500,

        colors: [theme.palette.text.secondary],
        fontSize: theme.typography.body1.fontSize as string,
      },
    },
    colors,
    states: {
      hover: {
        filter: { type: "none" },
      },
      active: {
        filter: { type: "none" },
      },
    },
    grid: {
      show: false,
    },
    xaxis: {
      axisTicks: { show: false },
      axisBorder: { color: theme.palette.divider },
      categories: xAxisData,
      labels: {
        style: {
          colors: theme.palette.text.disabled,
          fontFamily: theme.typography.fontFamily,
          fontSize: theme.typography.body2.fontSize as string,
        },
      },
    },
    yaxis: {
      labels: {
        formatter: (val) => (_.isNumber(val) ? formatNumber_q.format(val) : ""),
        style: {
          colors: theme.palette.text.disabled,
          fontFamily: theme.typography.fontFamily,
          fontSize: theme.typography.body2.fontSize as string,
        },
      },
    },
  };

  return (
    <Card className="h-100 w-100">
      <CardHeader
        className="p-0 ps-2"
        subheader={<Typography sx={{ color: "#1E355E" }}>Locates</Typography>}
      ></CardHeader>
      <CardContent
        className="h-100 pt-0"
        sx={{ "& .MuiTabPanel-root": { p: 0 } }}
      >
        <TabContext value={currentTab}>
          <TabList
            variant="scrollable"
            scrollButtons="auto"
            className="p-0"
            id="locate-bar-chart"
            onChange={handleChange}
            aria-label="earning report tabs"
            sx={{
              border: "0 !important",
              "& .MuiTabs-indicator": { display: "none" },
              "& .MuiTab-root": {
                p: 0,
                minWidth: 0,
                borderRadius: "10px",
                "&:not(:last-child)": { mr: 4 },
              },
            }}
          >
            {renderTabs(currentTab, theme)}
          </TabList>
          {renderTabPanels(currentTab, theme, options, colors)}
        </TabContext>
      </CardContent>
    </Card>
  );
};

export default LocatesBarChart;
